import type { ManualChapter } from '../types';

/** Rozdział instrukcji: Administrator (28.10.2026, S-28.10-02). */
export const chapter: ManualChapter = {
  key: 'admin',
  title: 'Administrator',
  audience: 'Dla administratora i administratora spółki — osób, które zakładają konta, ustawiają spółki, parametry i funkcje aplikacji.',
  intro:
    'Ekrany grupy „Administracja” w menu ustawiają to, na czym pracują wszystkie działy: konta i role, spółki, poświadczenia integracji, parametry rekomendacji i włączone funkcje. Każda zmiana zostawia ślad — kto, kiedy, co i dlaczego — w historii ekranu i w dzienniku działań.',
  sections: [
    {
      id: 'zakres-administratora',
      title: 'Administrator i administrator spółki',
      blocks: [
        {
          kind: 'text',
          text: 'Administrator ma dostęp do wszystkiego, także do ustawień obu spółek, i jego uprawnień nie da się odebrać. Administrator spółki zarządza kontami i ustawieniami swojej spółki, może nadawać role robocze (planista, trader, CS, Administracja, gość, administrator spółki), ale nie nadaje roli administratora i nie przypisuje kont do innej spółki. Ekrany „Spółki”, „Centrum ról i uprawnień” i „Pomiar użycia” są wyłącznie dla administratora.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Zmiany na ekranach administracyjnych wymagają powodu. Powód trafia do historii i dziennika działań — pisz go tak, żeby za pół roku ktoś inny zrozumiał, skąd wzięła się zmiana (np. „ustalenie z kierownikiem FTL — nowa osoba w dziale”).',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Ustawienia działają od razu dla wszystkich użytkowników, najpóźniej po minucie. Zanim zmienisz parametr albo funkcję w trakcie dnia pracy, uprzedź dział, którego to dotyczy.',
        },
      ],
    },
    {
      id: 'uzytkownicy',
      title: 'Użytkownicy',
      menu: ['users'],
      blocks: [
        {
          kind: 'text',
          text: 'Lista kont z rolą, spółką, telefonem i datą utworzenia. Filtr „Szukaj (nazwa / adres)” szuka po loginie i adresie e-mail, filtr „Rola” zawęża do jednej roli.',
        },
        {
          kind: 'image',
          image: { shot: 'users' },
          caption: 'Lista kont z rolą i spółką.',
        },
        {
          kind: 'task',
          title: 'Jak założyć konto nowemu pracownikowi',
          steps: [
            'Otwórz „Użytkownicy” w grupie „Administracja”.',
            'Kliknij „Utwórz”.',
            'Wpisz „Imię”, „Nazwisko”, „Nazwa użytkownika” i „Adres e-mail”.',
            'Wpisz „Hasło” startowe — co najmniej 12 znaków.',
            'Wybierz „Rola” zgodnie z działem pracownika i kliknij „Zapisz”.',
          ],
          outcome: 'Spółkę konta ustala system na podstawie Twojego konta. Przekaż pracownikowi adres e-mail i hasło osobnym kanałem, nie w jednej wiadomości.',
        },
        {
          kind: 'task',
          title: 'Jak zmienić rolę albo hasło',
          steps: [
            'Na liście „Użytkownicy” kliknij „Zmień” przy koncie.',
            'Wybierz nową „Rola” albo wpisz „Nowe hasło (opcjonalnie)” — puste pole hasła zostawia dotychczasowe.',
            'Kliknij „Zapisz”.',
          ],
          outcome: 'Nowe uprawnienia działają po ponownym zalogowaniu pracownika.',
        },
        {
          kind: 'terms',
          title: 'Role',
          items: [
            { term: 'Administrator', meaning: 'Wszystko, w obu spółkach; jedyny z dostępem do centrum ról, spółek i pomiaru użycia.' },
            { term: 'Administrator spółki', meaning: 'Konta, kontrahenci, poświadczenia, dziennik działań i parametry swojej spółki.' },
            { term: 'Planista główny FTL (master)', meaning: 'Team Leader FTL: obsadza pojazdy, akceptuje odpięcia, pracuje też w Trade.' },
            { term: 'Planista FTL (opiekun pojazdów)', meaning: 'Rekomendacje, warianty i flota — bez finalnego obsadzania.' },
            { term: 'Trader', meaning: 'Zlecenia Trade, giełdy, przewoźnicy i zapytania do przewoźników.' },
            { term: 'Team Leader Trade', meaning: 'Trader z decyzjami przełożonego w Trade.' },
            { term: 'CS / Administracja', meaning: 'Przyjęcie zleceń: skrzynka, prezlecenia, zapis do TMS.' },
            {
              term: 'Team Leader CS / Team Leader Administracji',
              meaning: 'Przełożeni działów przyjęcia zleceń — do nadania, gdy działy CS i Administracji pracują w aplikacji.',
              when: { on: ['intakeDepartments'] },
            },
            { term: 'Użytkownik', meaning: 'Rola ogólna z prawem do pracy we wszystkich działach roboczych.' },
            { term: 'Gość', meaning: 'Tylko podgląd banku i kart zleceń — bez żadnej zmiany danych.' },
            { term: 'Konto maszynowe', meaning: 'Konto dla systemu zewnętrznego, nie dla człowieka — nie nadawaj go pracownikom.' },
          ],
        },
        {
          kind: 'text',
          when: { on: ['publicationExceptionRequests'] },
          text: 'Na koncie jest też sekcja „Uprawnienia imienne”: np. „Akceptacja wyjątku od zakazu podwykonawcy” dla osoby spoza administracji, która rozpatruje wnioski traderów. Zapis wymaga powodu i trafia do dziennika działań.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Nie zakładaj kont wspólnych dla kilku osób — historia zmian zlecenia mówi, kto co zrobił, tylko wtedy, gdy każdy pracuje na swoim koncie.',
        },
      ],
    },
    {
      id: 'zaloguj-jako',
      title: 'Zaloguj jako inny użytkownik',
      when: { on: ['impersonation'] },
      blocks: [
        {
          kind: 'text',
          text: 'Gdy pracownik zgłasza problem albo potrzebuje pomocy, możesz zobaczyć aplikację jego oczami — bez jego hasła i bez wylogowania się ze swojego konta. Przycisk „Zaloguj jako” stoi na liście użytkowników (ikona przy koncie) i na karcie konta. Administrator loguje się jako każdy pracownik, administrator spółki — tylko jako pracownicy swojej spółki. Nie da się zalogować jako Ty sam, jako administrator ani administrator spółki ani jako konto maszynowe.',
        },
        {
          kind: 'image',
          image: { guide: 'impersonation', step: 3 },
          caption: 'Okno „Zaloguj jako” z polem powodu.',
        },
        {
          kind: 'task',
          title: 'Jak zalogować się jako pracownik',
          steps: [
            'Otwórz „Użytkownicy” i kliknij ikonę „Zaloguj jako” przy koncie pracownika (albo przycisk na karcie jego konta).',
            'Wpisz „Powód” — co najmniej 10 znaków, np. „zgłoszenie: nie widzi zlecenia w banku”.',
            'Kliknij „Zaloguj jako”. Otworzy się nowa karta z paskiem „Pracujesz jako … — zalogowano przez …”.',
            'Gdy skończysz, kliknij „Zakończ” w pasku.',
          ],
          outcome: 'Karta przejęcia zamyka się, a Twoja karta przez cały czas pracuje na Twoim koncie.',
        },
        {
          kind: 'list',
          title: 'Co działa w karcie „Zaloguj jako”',
          items: [
            'Pracujesz jak ten pracownik: widzisz jego menu, sekcje i dane, z jego rolą, uprawnieniami i spółką.',
            'Sesję kończy przycisk „Zakończ” w pasku albo upływ godziny (sesja się nie przedłuża) — zamknięcie karty jej nie kończy.',
            'Decyzje, zgody, publikacja na giełdy i zatwierdzenie zapisu do TMS działają jak u pracownika, ale wniosku złożonego w tej karcie nie rozstrzygniesz potem na swoim koncie (i odwrotnie) — zrobi to inna osoba.',
            'Inne karty przeglądarki działają na Twoim koncie; dopóki sesja trwa, pokazują ostrzeżenie „W innej karcie trwa praca jako …”. Karta otwarta odnośnikiem z karty „Zaloguj jako” też jest na Twoim koncie.',
            'Nie zmienisz kont, ról, haseł, poświadczeń integracji ani ustawień konta pracownika (profil, powiadomienia, zapisane widoki) i nie zalogujesz się jako kolejna osoba.',
            'Każda zmiana zapisana w tej karcie trafia do dziennika działań z Twoim nazwiskiem obok konta pracownika; początek i koniec — z powodem i godziną.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Zmiany, które zapiszesz w karcie przejęcia, w danych zlecenia widać jako zmiany pracownika — prawdziwego autora pokazuje dziennik działań (filtr „W czasie przejęcia”). Zmieniaj dane tylko wtedy, gdy pracownik o to prosi albo wie, że to robisz. Pomiar użycia nie liczy pracy w karcie przejęcia jako pracy pracownika.',
        },
      ],
    },
    {
      id: 'centrum-rol',
      title: 'Centrum ról i uprawnień',
      when: { on: ['roleCenter'] },
      menu: ['role-center'],
      blocks: [
        {
          kind: 'text',
          text: 'Tu określasz, co może każda rola i co widzi w aplikacji. Role są kartami z opisem, liczbą kont i uprawnień; rola systemowa ma znacznik „systemowa”, administrator — kłódkę „stała”. Zakładki ekranu: „Role”, „Macierz uprawnień”, „Macierz widoczności”, „Zobacz jako” i „Historia zmian”. Karta jednej roli ma własne zakładki: „Uprawnienia”, „Co widzi”, „Użytkownicy” i „Historia”.',
        },
        {
          kind: 'image',
          image: { guide: 'roleCenter', step: 1 },
          caption: 'Lista ról w centrum ról i uprawnień.',
        },
        {
          kind: 'task',
          title: 'Jak zmienić uprawnienia roli',
          steps: [
            'Otwórz „Centrum ról i uprawnień” i kliknij kartę roli.',
            'Na zakładce „Uprawnienia” przestaw wybrane uprawnienia — zmienione pozycje dostają znacznik „zmienione”.',
            'Kliknij „Zapisz zmiany (N)”, sprawdź listę „było → jest” i wpisz „Powód zmiany”.',
            'Potwierdź „Zapisz zmiany”.',
          ],
          outcome: 'Zmiana trafia do historii roli i dziennika działań.',
        },
        {
          kind: 'task',
          title: 'Jak dodać własną rolę',
          steps: [
            'Kliknij „Dodaj rolę”.',
            'Wpisz „Nazwa roli” i „Opis — co ta rola robi”, wybierz ikonę.',
            'W polu „Na wzór roli (uprawnienia i widoczność)” wybierz rolę, od której zaczynasz.',
            'Kliknij „Dodaj rolę”, a potem dopasuj uprawnienia na karcie nowej roli.',
          ],
          outcome: 'Rolę aktywną można nadać kontom na ekranie „Użytkownicy”.',
        },
        {
          kind: 'task',
          title: 'Jak sprawdzić, co zobaczy pracownik danej roli',
          steps: [
            'Wybierz zakładkę „Zobacz jako”.',
            'Wybierz rolę z listy.',
          ],
          outcome: 'Zobaczysz menu oraz listę sekcji, ekranów i kafelków, które rola widzi i których nie widzi — bez przelogowania.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Ukrycie elementu na zakładce „Co widzi” niczego nie odbiera — o dostępie rozstrzyga serwer według uprawnień. Element, do którego rola nie ma uprawnienia, jest wyszarzony z wyjaśnieniem. Gość nigdy nie dostanie prawa do zmian danych, a administrator ma zawsze wszystko.',
        },
      ],
    },
    {
      id: 'spolki',
      title: 'Spółki',
      menu: ['companies'],
      blocks: [
        {
          kind: 'text',
          text: 'Jedna aplikacja obsługuje obie spółki Marathonu. Ekran „Spółki” pokazuje dla każdej: kraj, identyfikator w VIA, tryb maskowania lokalizacji na giełdach, „Trade i giełdy”, moduły, stawki kosztu dolotu i liczbę pojazdów i zleceń w systemie.',
        },
        {
          kind: 'terms',
          title: 'Kolumny i znaczniki',
          items: [
            { term: '„Trade i giełdy”: „dozwolone” / „zablokowane”', meaning: 'Czy zlecenia spółki można przekazać do Trade i publikować na giełdach. Spółka bez kont giełdowych ma blokadę — jej zlecenia zostają do planowania floty.' },
            { term: '„Maskowanie lokalizacji”', meaning: 'Jak skracamy adres w ofercie na giełdzie: „Skrócony kod pocztowy” albo „Tylko miasto”. „wybór przy publikacji” — trader wybiera przy każdej publikacji. Dokładny adres nie idzie na giełdę nigdy.' },
            { term: '„Moduły”', meaning: 'Rekomendacje pojazdów, przewoźnicy, zapytania do przewoźników, giełdy, harmonogram kierowców i rekomendacje dla zewnętrznego systemu planowania. Domyślnie wszystko włączone; wyłączony moduł znika z menu i kart, a serwer odmawia jego użycia.' },
            { term: '„Koszt dolotu”: „komplet” / „brak N z M”', meaning: 'Stawki do liczenia kosztu dolotu (praca kierowcy, zestaw, spalanie, paliwo). Brak stawki = koszt nieznany, nigdy zero.' },
            {
              term: '„Konto giełdowe”',
              meaning: 'Czy spółka publikuje z własnych kont giełdowych, czy z kont drugiej spółki.',
              when: { on: ['exchangeBothCompanies'] },
            },
          ],
        },
        {
          kind: 'task',
          title: 'Jak zdjąć albo założyć blokadę Trade i giełd',
          steps: [
            'Otwórz „Spółki” w grupie „Administracja”.',
            'W kolumnie „Trade i giełdy” kliknij „Zdejmij blokadę” albo „Zablokuj”.',
            'Wpisz „Powód” i kliknij „Zapisz”.',
          ],
          outcome: 'Zdejmij blokadę dopiero wtedy, gdy spółka ma konto na giełdzie. Założenie blokady nie cofa zleceń już przekazanych do Trade.',
        },
        {
          kind: 'task',
          title: 'Jak wyłączyć moduł spółce',
          steps: [
            'W kolumnie „Moduły” przestaw przełącznik przy module.',
            'W oknie „Wyłączyć moduł …” wpisz „Powód” i kliknij „Zapisz”.',
          ],
          outcome: 'Modułu „Giełdy” nie wyłączysz, dopóki spółka ma wystawione oferty — najpierw trzeba je zdjąć.',
        },
        {
          kind: 'task',
          title: 'Jak ustawić stawki kosztu dolotu',
          steps: [
            'W kolumnie „Koszt dolotu” kliknij „Ustaw”.',
            'Wpisz stawki i wybierz „Waluta” przy każdej (PLN albo EUR).',
            'Kliknij „Zapisz”.',
          ],
          outcome: 'Stawki podaje Marathon. Kwoty w EUR system przelicza kursem średnim NBP z dnia załadunku.',
        },
        {
          kind: 'list',
          when: { on: ['parameterOwnership'] },
          title: 'Stawki z właścicielem i datą obowiązywania',
          items: [
            'W oknie stawek wpisz „Właściciel stawek” (puste — właściciel nieustalony) i „Powód zmiany” — bez powodu zapis się nie uda.',
            '„Obowiązuje od (czas polski)”: puste — zmiana działa od razu; data w przyszłości — do tego czasu koszt liczy obecna stawka, a w kolumnie widać znacznik „zaplanowane”.',
            '„Historia” przy kosztach dolotu pokazuje: kiedy, pole, było → jest, od kiedy, powód. Zmianę zaplanowaną odwołasz przyciskiem „Odwołaj zmianę” z powodem.',
          ],
        },
        {
          kind: 'image',
          when: { on: ['parameterOwnership'] },
          image: { guide: 'parameterOwnership', step: 4 },
          caption: 'Przycisk „Historia” przy spółce otwiera historię stawek kosztu.',
        },
      ],
    },
    {
      id: 'kontrahenci',
      title: 'Kontrahenci',
      menu: ['contractors'],
      blocks: [
        {
          kind: 'text',
          text: 'Kartoteka klientów z TMS: nazwa, adres, stan (np. „zablokowany” z powodem blokady) i powiązania ze zleceniami. Filtry: „Nazwa, NIP, miasto”, „Tylko zablokowani”, „Tylko aktywni w TMS”, „Spoza TMS”. Karta kontrahenta pokazuje kontakty, ostatnie zlecenia i „Priorytet klienta”.',
        },
        {
          kind: 'task',
          title: 'Jak wpisać regułę podwykonawcy dla klienta',
          intro: 'Reguła mówi, czy zlecenie klienta wolno oddać przewoźnikowi przez giełdę.',
          steps: [
            'Otwórz „Kontrahenci” i kliknij klienta.',
            'W sekcji „Podwykonawca — reguła publikacji na giełdach” wybierz „Reguła”.',
            'W „Uwagi” wpisz, skąd reguła (np. wiadomość od klienta z datą).',
            'Kliknij „Zapisz regułę”.',
          ],
          outcome: 'Zmianę widać w historii reguły na karcie kontrahenta.',
        },
        {
          kind: 'terms',
          title: 'Reguły podwykonawcy',
          items: [
            { term: '„podwykonawca dozwolony”', meaning: 'Trader może publikować zlecenia klienta na giełdzie.' },
            { term: '„podwykonawca dozwolony warunkowo”', meaning: 'Przed publikacją trader ręcznie potwierdza warunki klienta.' },
            { term: '„zakaz podwykonawstwa”', meaning: 'Publikacja tylko jako wyjątek zatwierdzony przez uprawnioną osobę, z powodem.' },
            { term: '„reguła nieustalona”', meaning: 'Brak reguły to nie zgoda — trader musi ręcznie potwierdzić publikację.' },
          ],
        },
        {
          kind: 'text',
          text: 'Szczegóły publikacji i bramek giełdowych opisuje rozdział Trade.',
        },
      ],
    },
    {
      id: 'poswiadczenia',
      title: 'Poświadczenia',
      menu: ['credentials'],
      blocks: [
        {
          kind: 'text',
          text: 'Klucze i hasła integracji (TMS, eco-truck, Timocom, Trans.eu) są przechowywane zaszyfrowane. Ekran pokazuje, która integracja ma zapisane poświadczenie, do czego służy i kiedy je ostatnio zmieniono — wartość zawsze jako maska „••••••••”. Każde użycie poświadczenia zapisuje się w historii.',
        },
        {
          kind: 'task',
          title: 'Jak zmienić login albo hasło serwera, przez który pliki trafiają do TMS',
          steps: [
            'Otwórz „Poświadczenia” w grupie „Administracja”.',
            'W części „Serwer SFTP importu TMS” wpisz nową „Nazwa użytkownika” albo „Hasło” — puste pole zostawia dotychczasową wartość.',
            'Kliknij „Zapisz w magazynie”.',
          ],
          outcome: 'Pojawi się komunikat „Zapisano w magazynie poświadczeń — wartości nie da się tu odczytać”. Pola nigdy nie pokazują zapisanych wartości.',
        },
        {
          kind: 'task',
          when: { on: ['ecoTruckConnectionCheck'] },
          title: 'Jak sprawdzić połączenie z eco-truckiem',
          steps: [
            'Na ekranie „Poświadczenia” znajdź wiersz „eco-truck (telematyka):”.',
            'Kliknij „Sprawdź połączenie”.',
          ],
          outcome: 'Wynik z godziną sprawdzenia: działa, klucz odrzucony, brak połączenia albo brak poświadczenia. Sprawdzenie wysyła jedno zapytanie i niczego nie zmienia.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Pozostałe klucze integracji wprowadza się po stronie serwera, nie na tym ekranie. Nigdy nie przesyłaj kluczy ani haseł w wiadomościach i nie wpisuj ich do uwag zleceń.',
        },
      ],
    },
    {
      id: 'dziennik-dzialan',
      title: 'Dziennik działań',
      menu: ['admin-log'],
      blocks: [
        {
          kind: 'text',
          text: 'Dziennik działań zbiera zmiany administracyjne i ustawień: konta, role, spółki, parametry, funkcje, reguły podwykonawców, profil użytkownika. Kolumny: „Kiedy”, „Działanie”, „Kto”, „Czego dotyczyło”. Filtr „Działanie” zawęża do utworzeń, zmian albo usunięć. Dane kontaktowe i treści haseł nie trafiają do dziennika.',
        },
        {
          kind: 'task',
          title: 'Jak sprawdzić, kto zmienił ustawienie',
          steps: [
            'Otwórz „Dziennik działań” w grupie „Administracja”.',
            'Wybierz w filtrze „Działanie” rodzaj zmiany i przejrzyj kolumnę „Czego dotyczyło”.',
            'Powód zmiany znajdziesz w historii ekranu, którego dotyczy wpis (np. historia parametru albo roli).',
          ],
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Historia zmian konkretnego zlecenia jest na karcie zlecenia, nie w dzienniku działań.',
        },
      ],
    },
    {
      id: 'parametry-rekomendacji',
      title: 'Parametry rekomendacji',
      menu: ['suggestion-parameters'],
      blocks: [
        {
          kind: 'text',
          text: 'Parametry, którymi liczą rekomendacje pojazdów i alerty: horyzont podpowiedzi, okno pozycji z historii zleceń, maksymalny wiek odczytu GPS, domyślna ładowność zestawu, progi marży i inne. Każdy wiersz pokazuje wartość, dozwolony zakres i ostatnią zmianę; pod tabelami jest historia zmian (kto, kiedy, z czego na co, powód). Wartości domyślne to nasze założenia — zmieniaj je po ustaleniu z działem.',
        },
        {
          kind: 'task',
          title: 'Jak zmienić parametr',
          steps: [
            'Otwórz „Parametry rekomendacji” w grupie „Administracja”.',
            'Kliknij „Zmień” przy parametrze.',
            'Wpisz wartość z zakresu podanego przy polu i „Powód zmiany”.',
            'Zapisz — zobaczysz komunikat „Parametr zapisany”.',
          ],
          outcome: 'Rekomendacje liczą nową wartością najpóźniej po minucie. „Historia” przy parametrze pokazuje wszystkie jego zmiany.',
        },
        {
          kind: 'terms',
          title: 'Wybrane parametry i sekcje',
          items: [
            { term: '„Horyzont podpowiedzi”', meaning: 'Na ile dni do przodu „zdąży” jest liczone z dzisiejszej pozycji auta; dalej to brak danych.' },
            { term: '„Maksymalny wiek odczytu GPS”', meaning: 'Starszy odczyt nie jest pozycją — liczy się rozładunek ostatniego zlecenia.' },
            { term: '„Domyślna ładowność zestawu”', meaning: 'Wartość, gdy kartoteka nie zna ładowności — wtedy tylko ostrzeżenie, nigdy wykluczenie.' },
            {
              term: '„Średnia prędkość przejazdu (model floty)”',
              meaning: 'Czas przejazdu bez tras HERE (dolot, „zdąży”, „dowiezie”, zajętość, koszt) liczy się tą prędkością — domyślnie 72,5 km/h.',
              when: { on: ['averageSpeedParameter'] },
            },
            {
              term: 'Średnia prędkość przejazdu',
              meaning: 'Czas przejazdu bez tras HERE liczy stała prędkość zmierzona na śladzie GPS floty; na ekranie nie ma jej parametru.',
              when: { off: ['averageSpeedParameter'] },
            },
            {
              term: '„Progi alertów i założenia robocze”',
              meaning: 'Okno alertu o przeplanowaniu, próg „lepsze auto”, najstarsza oś czasu z tachografu, progi podejrzanej daty z TMS i liczba zajętych aut na końcu listy rekomendacji. Wartości domyślne = dotychczasowe ustawienia.',
              when: { on: ['alertThresholdParameters'] },
            },
            {
              term: '„Region na pulpitach FTL”',
              meaning: 'Ile pierwszych cyfr kodu pocztowego tworzy „mój region” planisty — osobno dla każdego kraju (domyślnie 2, w krajach małych 1).',
              when: { on: ['regionDigitsByCountry'] },
            },
          ],
        },
        {
          kind: 'task',
          when: { on: ['regionDigitsByCountry'] },
          title: 'Jak ustawić region dla nowego kraju',
          steps: [
            'Przewiń do sekcji „Region na pulpitach FTL”.',
            'Kliknij „Dodaj kraj”.',
            'Wpisz „Kraj (kod ISO)”, „Cyfry kodu, zakres 0–2” i „Powód zmiany” i kliknij „Zapisz”.',
          ],
          outcome: 'Kraj spoza tabeli liczy się wartością domyślną. Pojazd znany tylko z pozycji GPS daje region krajowy.',
        },
        {
          kind: 'image',
          when: { on: ['regionDigitsByCountry'] },
          image: { guide: 'regionDigitsByCountry', step: 1 },
          caption: 'Sekcja „Region na pulpitach FTL”.',
        },
        {
          kind: 'list',
          when: { on: ['parameterOwnership'] },
          title: 'Właściciel i data obowiązywania',
          items: [
            'Przy każdym parametrze stoi właściciel biznesowy („Właściciel” — z powodem) i od kiedy obowiązuje wartość.',
            'W oknie „Zmień” pole „Obowiązuje od (czas polski)”: puste — zmiana działa od razu; data w przyszłości — zmiana zaplanowana, do tego czasu obowiązuje obecna wartość.',
            'Zmianę zaplanowaną odwołasz przyciskiem „Odwołaj” z powodem.',
          ],
        },
        {
          kind: 'image',
          when: { on: ['parameterOwnership'] },
          image: { guide: 'parameterOwnership', step: 1 },
          caption: 'Właściciel i obowiązywanie przy parametrach.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Parametry zmieniają podpowiedzi dla wszystkich planistów naraz. Puste pole progu marży oznacza alert wyłączony, a nie zero.',
        },
      ],
    },
    {
      id: 'przelaczniki',
      title: 'Przełączniki funkcji i paczki wydania',
      menu: ['feature-flags'],
      blocks: [
        {
          kind: 'text',
          text: 'Nowe funkcje stoją za przełącznikami i czekają na decyzję Marathonu. Ekran pokazuje każdą funkcję z opisem, „Stan tutaj” (włączona / wyłączona na tym środowisku), „Decyzja” i „Komentarz decyzji”. Stan i decyzja to dwie różne rzeczy: decyzję zapisuje się osobno, a włączenie na produkcji następuje dopiero po decyzji „wchodzi”.',
        },
        {
          kind: 'image',
          image: { shot: 'feature-flags' },
          caption: 'Ekran przełączników z paczkami wydania.',
        },
        {
          kind: 'terms',
          title: 'Paczki wydania i decyzje',
          items: [
            { term: 'Paczki wydania', meaning: 'Grupy funkcji wydawane razem: „Nowy FTL”, „Nowy Trade”, „CS i Administracja” oraz „Nowy wygląd i instrukcja obsługi” (nowy wygląd wszystkich ekranów i ekran „Instrukcja”). Wyłączona paczka = jej funkcje działają jak dotąd, w wersji obecnej. Opcja „Przy wyłączonej paczce ukryj też z menu: …” chowa wskazane ekrany.' },
            { term: '„do decyzji”', meaning: 'Funkcja czeka na decyzję Marathonu.' },
            { term: '„wchodzi”', meaning: 'Decyzja zapadła — po wydaniu administrator włącza funkcję na produkcji.' },
            { term: '„do rozbudowy”', meaning: 'Funkcja wymaga zmian; komentarz musi powiedzieć, czego.' },
            { term: '„odrzucone”', meaning: 'Funkcja odpada; komentarz musi powiedzieć, dlaczego. Na produkcji zostaje wyłączona.' },
            { term: '„w paczce wydania”', meaning: 'Funkcja należy do wydawanej paczki; funkcji spoza paczki nie da się włączyć na środowisku z profilem wydania.' },
          ],
        },
        {
          kind: 'task',
          title: 'Jak włączyć albo wyłączyć funkcję',
          steps: [
            'Otwórz „Przełączniki funkcji” w grupie „Administracja”.',
            'Znajdź funkcję (filtr „Decyzja” zawęża listę) i kliknij „Włącz” albo „Wyłącz”.',
            'Wpisz „Powód zmiany”, np. z kim i kiedy to ustalono, i kliknij „Włącz” albo „Wyłącz” w oknie.',
          ],
          outcome: 'Zmiana działa najpóźniej po minucie, a „Historia” przy funkcji pokazuje historię stanu i decyzji.',
        },
        {
          kind: 'task',
          title: 'Jak zapisać decyzję o funkcji',
          steps: [
            'Kliknij „Decyzja” przy funkcji.',
            'Wybierz „wchodzi”, „do rozbudowy”, „odrzucone” albo „do decyzji”.',
            'Wpisz „Komentarz decyzji” — przy „do rozbudowy” i „odrzucone” jest wymagany — i kliknij „Zapisz decyzję”.',
          ],
        },
        {
          kind: 'task',
          title: 'Jak włączyć albo wyłączyć całą paczkę',
          steps: [
            'W części „Paczki wydania” kliknij „Włącz paczkę” albo „Wyłącz paczkę”.',
            'Wpisz „Powód” — trafi do historii paczki i dziennika działań — i kliknij „Zapisz”.',
          ],
          outcome: 'Wyłączenie paczki od razu przywraca jej funkcje do wersji obecnej dla wszystkich.',
        },
        {
          kind: 'text',
          text: 'Na środowisku akceptacyjnym wersja „obecna (jak na produkcji)” działa jak produkcja: funkcje paczki wydanej na produkcji działają w niej tylko przy włączonej paczce — karta paczki mówi to wprost. Wersja „nowa” pokazuje funkcje według pojedynczych przełączników.',
        },
        {
          kind: 'text',
          when: { on: ['guideOverlay'] },
          text: 'Przycisk „Przewodnik” przy funkcji otwiera przewodnik po zmianach na jej pierwszym kroku — przydatne przed zapisaniem decyzji.',
        },
        {
          kind: 'text',
          when: { on: ['mockupLook'] },
          text: 'Funkcja nowego wyglądu aplikacji zmienia tylko wygląd wszystkich ekranów — kolory, ramki sekcji z niebieskim znacznikiem, nagłówki tabel, znaczniki stanów i liczniki pulpitów. Dane i działanie ekranów zostają te same, a tekst zawsze się mieści: zawija się albo ma dymek z pełną treścią. Po wyłączeniu aplikacja od razu wraca do dotychczasowego wyglądu.',
        },
      ],
    },
    {
      id: 'kto-realizuje-wg-tms',
      title: 'Kto realizuje wg TMS',
      when: { on: ['executionDepartmentHint'] },
      menu: ['execution-department-mappings'],
      blocks: [
        {
          kind: 'text',
          text: 'Pole TMS „Dział odpowiedzialny” mówi, czy zlecenie realizuje flota, czy Trade — zwykle jest puste. Tu wpisujesz, która wartość pola oznacza flotę, a która Trade. Zlecenia z taką wartością dostają znacznik „wg TMS: Trade” albo „wg TMS: flota” na liście i karcie oraz filtr „Realizacja wg TMS”.',
        },
        {
          kind: 'task',
          title: 'Jak przypisać wartość pola z TMS do działu',
          steps: [
            'Otwórz „Kto realizuje wg TMS”.',
            'W tabeli „Wartości w zleceniach” kliknij „Dodaj” przy wartości (albo „Dodaj wartość” nad mapowaniem).',
            'Wybierz „Realizuje” (flota albo Trade), wpisz „Powód” i kliknij „Zapisz”.',
          ],
          outcome: 'Wpis zmienisz albo usuniesz przyciskami „Zmień” i „Usuń”; każda zmiana trafia do historii.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'To tylko podpowiedź — do Trade przekazuje człowiek.',
        },
      ],
    },
    {
      id: 'kalendarz-zakazow',
      title: 'Kalendarz zakazów',
      when: { on: ['truckBanCalendar'] },
      menu: ['truck-bans'],
      blocks: [
        {
          kind: 'text',
          text: 'Podgląd tylko do odczytu: zakazy ruchu ciężarówek z przepisów krajowych — „Kraje”, „Zakazy tygodniowe” i „Święta i dni szczególne”, każdy wiersz z podstawą prawną i źródłem. Godziny to czas lokalny kraju. Landy, kantony i odcinki dróg są tylko opisane.',
        },
        {
          kind: 'task',
          title: 'Jak sprawdzić zakazy w kraju',
          steps: [
            'Otwórz „Kalendarz zakazów”.',
            'Wybierz „Kraj” i przejrzyj zakazy tygodniowe i święta.',
          ],
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Nad listą rekomendacji pojawia się ostrzeżenie „Przejazd może trafić na zakaz ruchu ciężarówek w …”, gdy okno załadunku albo rozładunku nachodzi na zakaz w kraju punktu. Ostrzeżenie nie zmienia „zdąży”, „dowiezie”, kolejności ani wykluczeń; przejazdu przez inne kraje nie sprawdzamy.',
        },
      ],
    },
    {
      id: 'powody-przekazania',
      title: 'Powody przekazania do Trade',
      when: { on: ['tradeTransferReasons'] },
      menu: ['trade-transfer-reasons'],
      blocks: [
        {
          kind: 'text',
          text: 'Dwie listy powodów: „Przekazanie do Trade” i „Zwrot z Trade do FTL”. Pracownik wybiera powód z listy przy przekazaniu albo zwrocie i dopisuje komentarz. Powodów się nie usuwa, bo są w historii zleceń — zdejmuje się je z listy (znacznik „zdjęty z listy”).',
        },
        {
          kind: 'task',
          title: 'Jak dodać albo zmienić powód',
          steps: [
            'Otwórz „Powody przekazania do Trade”.',
            'Kliknij „Dodaj powód” albo „Zmień” przy istniejącym.',
            'Wybierz „Dotyczy”, wpisz „Treść powodu”, ustaw „Kolejność”, „Na liście do wyboru” i — dla powodów ogólnych — „Wymaga komentarza (np. „inny powód”)”.',
            'Wpisz „Powód zmiany” i kliknij „Zapisz”.',
          ],
        },
      ],
    },
    {
      id: 'pomiar-uzycia',
      title: 'Pomiar użycia',
      when: { on: ['usageTelemetry'] },
      menu: ['usage-analytics'],
      blocks: [
        {
          kind: 'text',
          text: 'Aplikacja zapisuje, kto z jakiego działu wchodzi na które ekrany, klika które przyciski, zatwierdza które okna i trafia na błędy — z numerem zlecenia, żeby złożyć przejście zlecenia przez działy. Treści pól, wartości filtrów i danych osobowych poza numerem konta nie zapisujemy. Ekran „Pomiar użycia” widzi tylko administrator.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'To pomiar pracy pracowników — przed pilotażem trzeba ich o nim poinformować.',
        },
        {
          kind: 'task',
          title: 'Jak sprawdzić, gdzie zatrzymują się zlecenia',
          steps: [
            'Otwórz „Pomiar użycia” w grupie „Administracja”.',
            'Ustaw okres („Od”, „Do”) i w razie potrzeby „Dział”, „Rola” albo „Konto”.',
            'Na zakładce „Ścieżki pracy” przejrzyj kolumny „Gdzie się zatrzymują” i „Mediana czasu”.',
            'Kliknij „Eksport CSV” nad tabelą, żeby zapisać ją do pliku. „Eksport wszystkich zdarzeń (CSV)” u góry ekranu pobiera wszystkie zdarzenia z okresu i filtrów.',
          ],
        },
        {
          kind: 'image',
          image: { guide: 'usageTelemetry', step: 3 },
          caption: 'Ścieżki zleceń przez działy.',
        },
        {
          kind: 'terms',
          title: 'Zakładki',
          items: [
            { term: '„Ekrany i przyciski”', meaning: 'Najczęściej używane ekrany i przyciski.' },
            { term: '„Działania” i „Kliknięcia”', meaning: 'Każde zapisanie i zmiana oraz kliknięcia z napisem z ekranu.' },
            { term: '„Oś czasu konta”', meaning: 'Po wybraniu konta w filtrze — po kolei, co i kiedy zrobiło.' },
            { term: '„Odmowy i błędy”', meaning: 'Odmowy dostępu i błędy według ekranu.' },
            { term: '„Aktywność dzienna”', meaning: 'Aktywność działów dzień po dniu.' },
          ],
        },
        {
          kind: 'task',
          title: 'Jak usunąć starsze dane pomiaru',
          steps: [
            'Kliknij „Usuń dane starsze niż…”.',
            'W polu „Usuń zdarzenia sprzed dnia” wybierz dzień i sprawdź, ile zdarzeń zniknie.',
            'Wpisz „Powód usunięcia” i kliknij „Usuń bezpowrotnie” — bez powodu przycisk jest nieaktywny.',
          ],
          outcome: 'Usunięcie dotyczy całego pomiaru, nie tylko bieżących filtrów, i trafia do dziennika działań. Domyślnie zdarzenia przechowujemy bez limitu — same nie znikają. Liczba dni w parametrze „Pomiar użycia: przechowywanie zdarzeń” na ekranie „Parametry rekomendacji” włącza codzienne usuwanie starszych; 0 to bez limitu.',
        },
      ],
    },
  ],
};
